import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const AuthCallback = () => {
  const { completeSessionExchange } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      setError('Missing session token. Please sign in again.');
      return;
    }
    const sessionId = decodeURIComponent(match[1]);

    (async () => {
      try {
        const user = await completeSessionExchange(sessionId);
        // Strip the hash and route by role
        if (typeof window !== 'undefined' && window.history?.replaceState) {
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }
        const dest =
          user.role === 'employee' ? '/employee-portal'
          : user.role === 'partner' ? '/partner-portal'
          : '/';
        navigate(dest, { replace: true, state: { user, justSignedIn: true } });
      } catch (e) {
        const detail = e?.response?.data?.detail || 'Sign-in failed. Please try again.';
        setError(typeof detail === 'string' ? detail : 'Sign-in failed. Please try again.');
      }
    })();
  }, [completeSessionExchange, navigate, location]);

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-6">
      <div className="text-center max-w-md">
        {error ? (
          <>
            <div className="w-14 h-14 rounded-full bg-red-500/15 border border-red-500/40 mx-auto flex items-center justify-center">
              <AlertCircle className="w-7 h-7 text-red-400" />
            </div>
            <h1 className="mt-5 text-[22px] font-semibold text-white">Sign-in failed</h1>
            <p className="mt-2 text-[14px] text-neutral-400 leading-relaxed">{error}</p>
            <button
              onClick={() => navigate('/', { replace: true })}
              className="mt-6 h-10 px-5 rounded-full bg-white text-neutral-950 hover:bg-neutral-200 text-[14px] font-medium"
            >
              Back to home
            </button>
          </>
        ) : (
          <>
            <Loader2 className="w-7 h-7 text-blue-400 animate-spin mx-auto" />
            <h1 className="mt-5 text-[20px] font-semibold text-white">Signing you in…</h1>
            <p className="mt-2 text-[13.5px] text-neutral-400">Just a moment while we verify your Google session.</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
